import React from 'react';
import { FailureStageEntry, PipelineStageSummary } from './types';

// Shared between the Failed PRs tab (QueueEvictions.tsx) and the Overview tab's
// Recent MQ Failures widget (MergeQueue.tsx) so both render a build's failure exactly
// the same way — same badge styling, same links, same fallback for an unresolved stage.

const stageLabel = (s: { name: string; status: 'FAILED' | 'UNSTABLE' | 'ABORTED' }) =>
  `${s.name}${s.status === 'FAILED' ? ' Failed' : s.status === 'UNSTABLE' ? ' Unstable' : ' Aborted'}`;

// Deep-links straight to the Jenkins console log instead of the build's landing page,
// which otherwise takes an extra click via "Console Output" in the sidebar — except
// CloudBees' own pipeline explorer view (already shows everything on one page) and
// Allure report links (a test report, not a Jenkins build page).
const withConsolePath = (url: string) =>
  /\/(cloudbees-pipeline-explorer|allure)\/?$/.test(url) ? url : `${url.replace(/\/+$/, '')}/console`;

// Every stage badge links out to a build — the trailing "↗" indicates that, with an
// underline on hover. showStatusSuffix controls whether " Failed"/" Unstable" is
// appended to the name — the Failure Stage column omits it (the name alone, e.g.
// "web-e2e", says enough), while the Failure Reason column keeps it (e.g. "Checks Failed").
const clickableStageBadge = (
  s: { name: string; status: 'FAILED' | 'UNSTABLE' | 'ABORTED' },
  url: string,
  showStatusSuffix = true
) => (
  <a
    key={`${s.name}-${url}`}
    href={withConsolePath(url)}
    target="_blank"
    rel="noopener noreferrer"
    className={`pipeline-stage-badge pipeline-stage-badge--${s.status.toLowerCase()} pipeline-stage-badge--link`}
    title={s.status}
  >
    {showStatusSuffix ? stageLabel(s) : s.name} ↗
  </a>
);

// Catch-all for when no specific stage could be resolved (e.g. the wfapi lookup itself
// failed, or the failure happened outside any stage) — still surface it as a link to
// the job rather than leaving the row with no failure reason at all.
const errorBadge = (url: string) => (
  <a
    href={withConsolePath(url)}
    target="_blank"
    rel="noopener noreferrer"
    className="pipeline-stage-badge pipeline-stage-badge--failed pipeline-stage-badge--link"
    title="FAILED"
  >
    Error ↗
  </a>
);

export const failureReasonContent = (stages: PipelineStageSummary[] | undefined, parentBuildUrl: string) =>
  stages && stages.length > 0
    ? stages.map(s => clickableStageBadge(s, parentBuildUrl))
    : errorBadge(parentBuildUrl);

// A build can fan out to several parallel child jobs (docker builds, e2e/smoke tests) —
// each failing one gets its own badge with its own link (e.g. its own Allure report).
const failureStageBadge = (s: FailureStageEntry) => {
  const className = `pipeline-stage-badge pipeline-stage-badge--${s.status.toLowerCase()}${s.url ? ' pipeline-stage-badge--link' : ''}`;
  return s.url ? (
    <a key={`${s.name}-${s.url}`} href={withConsolePath(s.url)} target="_blank" rel="noopener noreferrer" className={className} title={s.status}>
      {s.name} ↗
    </a>
  ) : (
    <span key={s.name} className={className} title={s.status}>
      {s.name}
    </span>
  );
};

export const failureStageContent = (stages: FailureStageEntry[] | undefined) =>
  stages && stages.length > 0 ? stages.map(failureStageBadge) : null;
